import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import PortalLayout from "../portal/PortalLayout";
import { StatusPill, SERVICE_LABELS, formatDate, STATUS_META } from "../portal/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Briefcase, Users, AlertTriangle, CheckCircle2, Clock, ShieldAlert, Car, Search, UserCheck, Pencil, Send } from "lucide-react";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5DDC9] p-4 sm:p-5">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-[#64748B]">{label}</span>
        <Icon className={`w-4 h-4 ${accent || "text-[#AC9667]"}`} />
      </div>
      <p className="font-serif text-3xl text-[#005165]">{value ?? "—"}</p>
    </div>
  );
}

function AssignCaregiverDialog({ caseItem, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ available_only: true, min_exp: 0 });
  const [search, setSearch] = useState("");

  const loadCaregivers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.available_only) params.available = true;
      if (filter.min_exp) params.min_experience = filter.min_exp;
      const { data } = await api.get("/admin/caregivers", { params });
      setCaregivers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadCaregivers();
    // eslint-disable-next-line
  }, [open, filter]);

  const assign = async (caregiverId) => {
    try {
      await api.post("/admin/assignments", { case_id: caseItem.id, caregiver_id: caregiverId });
      toast.success("Caregiver assigned. Offer sent.");
      setOpen(false);
      onAssigned?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = caregivers.filter((c) =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`assign-btn-${caseItem.id}`} size="sm" className="bg-[#AC9667] hover:bg-[#7A6A48] text-white rounded-full h-9 px-4">
          <UserCheck className="w-3.5 h-3.5 mr-1" /> Assign
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-[#FFFFFF] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Assign caregiver to {caseItem.client_name}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-3 items-center my-4">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
            <Input
              data-testid="caregiver-search"
              placeholder="Search caregivers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-white border-[#E5DDC9] h-10"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#005165]">
            <Switch checked={filter.available_only} onCheckedChange={(v) => setFilter((f) => ({ ...f, available_only: v }))} className="data-[state=checked]:bg-emerald-600" />
            Available only
          </label>
          <Select value={String(filter.min_exp)} onValueChange={(v) => setFilter((f) => ({ ...f, min_exp: parseInt(v) }))}>
            <SelectTrigger className="w-[160px] h-10 bg-white border-[#E5DDC9]"><SelectValue placeholder="Min experience" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Any experience</SelectItem>
              <SelectItem value="1">1+ years</SelectItem>
              <SelectItem value="3">3+ years</SelectItem>
              <SelectItem value="5">5+ years</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="text-sm text-[#64748B] py-6">Loading caregivers...</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[#64748B] py-6">No matching caregivers.</p>
        ) : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {filtered.map((c) => (
              <div key={c.user_id} data-testid={`caregiver-row-${c.user_id}`} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white border border-[#E5DDC9]">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#005165] truncate">{c.name}</span>
                    {c.is_available && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Available</span>}
                    {!c.approved && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Pending approval</span>}
                    {c.transportation && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 inline-flex items-center gap-0.5"><Car className="w-3 h-3" />Has car</span>}
                  </div>
                  <p className="text-xs text-[#64748B] truncate">
                    {(c.years_experience || 0)} yrs exp · {c.email} · {c.phone || "no phone"}
                  </p>
                </div>
                <Button
                  data-testid={`assign-${c.user_id}`}
                  disabled={!c.approved}
                  onClick={() => assign(c.user_id)}
                  size="sm"
                  className="bg-[#005165] hover:bg-[#003946] text-[#FFFFFF] rounded-full h-9 px-4 shrink-0 disabled:opacity-40"
                >
                  Assign
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CaseRow({ caseItem, onChange }) {
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState(caseItem.admin_notes || "");
  const [status, setStatus] = useState(caseItem.status);

  const save = async () => {
    try {
      await api.patch(`/cases/${caseItem.id}`, { admin_notes: notes, status });
      toast.success("Case updated.");
      setNotesOpen(false);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <article data-testid={`admin-case-${caseItem.id}`} className="bg-white rounded-2xl border border-[#E5DDC9] p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <p className="font-serif text-lg text-[#005165]">{caseItem.client_name}</p>
            <StatusPill status={caseItem.status} urgent={caseItem.urgent} />
          </div>
          <p className="text-xs text-[#64748B]">
            Care for <span className="text-[#005165]">{caseItem.care_recipient_name}</span> ({caseItem.relationship}) · Submitted {formatDate(caseItem.created_at)}
          </p>
          <p className="text-xs text-[#64748B] mt-0.5">
            {caseItem.client_email} {caseItem.client_phone ? `· ${caseItem.client_phone}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!["completed", "cancelled"].includes(caseItem.status) && (
            <AssignCaregiverDialog caseItem={caseItem} onAssigned={onChange} />
          )}
          <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
            <DialogTrigger asChild>
              <Button data-testid={`edit-case-${caseItem.id}`} variant="outline" size="sm" className="rounded-full h-9 px-4 border-[#005165]/20">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#FFFFFF]">
              <DialogHeader><DialogTitle className="font-serif">Update case</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="mt-1.5 bg-white border-[#E5DDC9]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_META).filter(([k]) => !["offered","accepted","declined"].includes(k)).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Admin notes (visible to client)</Label>
                  <Textarea data-testid={`notes-${caseItem.id}`} rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 bg-white border-[#E5DDC9]" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid={`save-case-${caseItem.id}`} onClick={save} className="bg-[#005165] text-[#FFFFFF] rounded-full">Save changes</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {caseItem.services?.map((s) => (
          <span key={s} className="px-2.5 py-0.5 rounded-full text-xs bg-[#F8F9FA] text-[#005165] border border-[#E5DDC9]">
            {SERVICE_LABELS[s] || s}
          </span>
        ))}
      </div>
      {caseItem.notes && <p className="text-sm text-[#4B5563] italic mb-2">"{caseItem.notes}"</p>}
      {caseItem.admin_notes && (
        <div className="mt-2 p-3 rounded-lg bg-[#F8F9FA]/40 border border-[#E5DDC9] text-sm">
          <p className="text-[10px] uppercase tracking-wider text-[#AC9667] mb-1">Admin notes</p>
          {caseItem.admin_notes}
        </div>
      )}
    </article>
  );
}

export function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [cases, setCases] = useState([]);
  const [tab, setTab] = useState("urgent");

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get("/admin/dashboard"), api.get("/cases")]);
      setStats(s.data);
      setCases(c.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, []);

  const buckets = useMemo(() => ({
    urgent: cases.filter((c) => c.urgent && !["completed","cancelled"].includes(c.status)),
    pending: cases.filter((c) => c.status === "pending"),
    active: cases.filter((c) => ["assigned","in_progress","under_review"].includes(c.status)),
    completed: cases.filter((c) => c.status === "completed"),
  }), [cases]);

  return (
    <PortalLayout title="Operations Dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
        <StatCard icon={AlertTriangle} label="Urgent" value={stats.urgent_cases} accent="text-rose-600" />
        <StatCard icon={Clock} label="Pending" value={stats.pending_cases} />
        <StatCard icon={Briefcase} label="Active" value={stats.active_cases} accent="text-emerald-600" />
        <StatCard icon={CheckCircle2} label="Completed" value={stats.completed_cases} />
        <StatCard icon={Users} label="Available" value={stats.available_caregivers} accent="text-emerald-600" />
        <StatCard icon={ShieldAlert} label="To approve" value={stats.pending_caregivers} accent="text-amber-600" />
        <StatCard icon={Send} label="Offers out" value={stats.pending_assignments} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#E5DDC9] mb-5">
          <TabsTrigger value="urgent" data-testid="tab-urgent">Urgent ({buckets.urgent.length})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending ({buckets.pending.length})</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">Active ({buckets.active.length})</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">Completed ({buckets.completed.length})</TabsTrigger>
        </TabsList>

        {Object.entries(buckets).map(([key, list]) => (
          <TabsContent key={key} value={key}>
            {list.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#E5DDC9] p-10 text-center text-sm text-[#64748B]">
                No cases in this bucket.
              </div>
            ) : (
              <div className="grid gap-3">
                {list.map((c) => <CaseRow key={c.id} caseItem={c} onChange={load} />)}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </PortalLayout>
  );
}

export function AdminCases() {
  return <AdminDashboard />;
}

export function AdminCaregivers() {
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ available_only: false, min_exp: 0, search: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.available_only) params.available = true;
      if (filter.min_exp) params.min_experience = filter.min_exp;
      const { data } = await api.get("/admin/caregivers", { params });
      setCaregivers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.available_only, filter.min_exp]);

  const approve = async (id) => {
    try {
      await api.patch(`/admin/caregivers/${id}/approve`);
      toast.success("Caregiver approved.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = caregivers.filter((c) =>
    !filter.search || c.name?.toLowerCase().includes(filter.search.toLowerCase()) || c.email?.toLowerCase().includes(filter.search.toLowerCase())
  );

  return (
    <PortalLayout title="Caregiver Roster">
      <div className="flex flex-wrap gap-3 items-center mb-6 bg-white border border-[#E5DDC9] rounded-2xl p-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
          <Input
            data-testid="cg-search"
            placeholder="Search by name or email..."
            value={filter.search}
            onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
            className="pl-9 bg-[#FFFFFF] border-[#E5DDC9] h-10"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch data-testid="cg-filter-available" checked={filter.available_only} onCheckedChange={(v) => setFilter((f) => ({ ...f, available_only: v }))} className="data-[state=checked]:bg-emerald-600" />
          Available only
        </label>
        <Select value={String(filter.min_exp)} onValueChange={(v) => setFilter((f) => ({ ...f, min_exp: parseInt(v) }))}>
          <SelectTrigger className="w-[160px] h-10 bg-[#FFFFFF] border-[#E5DDC9]"><SelectValue placeholder="Experience" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Any experience</SelectItem>
            <SelectItem value="1">1+ years</SelectItem>
            <SelectItem value="3">3+ years</SelectItem>
            <SelectItem value="5">5+ years</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-sm text-[#64748B] py-10">Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E5DDC9] p-10 text-center text-sm text-[#64748B]">
          No caregivers match the filters.
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((c) => (
            <article key={c.user_id} data-testid={`admin-cg-${c.user_id}`} className="bg-white rounded-2xl border border-[#E5DDC9] p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <p className="font-serif text-lg text-[#005165]">{c.name}</p>
                    {c.is_available && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Available</span>}
                    {!c.approved && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Pending</span>}
                    {c.transportation && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 inline-flex items-center gap-0.5"><Car className="w-3 h-3" />Has car</span>}
                  </div>
                  <p className="text-xs text-[#64748B] truncate">{c.email} · {c.phone || "no phone"}</p>
                  <p className="text-xs text-[#4B5563] mt-1">
                    {(c.years_experience || 0)} yrs · {c.certifications || "no certifications listed"}
                  </p>
                  {(c.availability_days?.length > 0 || c.availability_shifts?.length > 0) && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {c.availability_days?.map((d) => <span key={d} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F8F9FA] text-[#005165]">{d.toUpperCase()}</span>)}
                      {c.availability_shifts?.map((s) => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F8F9FA] text-[#005165]">{s}</span>)}
                    </div>
                  )}
                </div>
                {!c.approved && (
                  <Button
                    data-testid={`approve-${c.user_id}`}
                    onClick={() => approve(c.user_id)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-10 px-5 shrink-0"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
                  </Button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </PortalLayout>
  );
}
